/**
 * English rendering of catalog amounts — the `/en` twin of `format.ts`.
 *
 * **Same numbers, different words.** Both files read the identical
 * `CatalogProduct` fields the server sends; only the surrounding language
 * differs. That is the whole point: the Korean and English pages cannot show
 * different money, because neither of them owns any money.
 *
 * The previous English fee guide carried its own copy of every amount, with a
 * header saying they were "copied VERBATIM from the Korean fee guide". They
 * drifted anyway — `membership.test.ts` records the round where the Korean
 * table was corrected and the English one was not, and English customers were
 * sold a pricing model that no longer existed.
 *
 * **Amounts stay in KRW.** No USD conversion, ever. A rate would make the two
 * pages disagree the moment it moved, and the fee actually charged is in KRW.
 */

import type { CatalogProduct, Cta } from '@/lib/api/catalog';

/**
 * **This file holds no vocabulary.**
 *
 * It used to. `categoryLabel`, `surcharge.unitLabel` and `deliverables` arrived as
 * Korean free text in every locale, so three lookup tables lived here to supply an
 * English word for each.
 *
 * They covered exactly the products that were live at the time. That is the whole
 * problem: the moment an operator confirmed a ninth product and switched on English
 * exposure, its deliverables vanished from the page — unmapped entries were dropped
 * rather than printed in Korean — and nobody watching the admin screen would know.
 * A dictionary a developer has to grow is the hardcoded fee table in another shape.
 *
 * The wording is now product data, authored in the admin alongside the price, and a
 * `locale=en` response carries it. Turning on English exposure without an English
 * name is a 400, and so is a surcharge without an English unit.
 *
 * What is left here is formatting: currency, pluralisation, sentence assembly.
 *
 * **No amounts, and no multiplier.** The English price is the server's
 * `supplyAmount`, already scaled.
 */

/** `400000` → `"KRW 400,000"`. Grouping follows en-US; the currency does not change. */
export function formatKrw(amount: number): string {
  return `KRW ${amount.toLocaleString('en-US')}`;
}

/** Published fee, VAT excluded. */
export function supplyTextEn(product: CatalogProduct): string {
  return formatKrw(product.supplyAmount);
}

/** Amount payable, VAT included. */
export function totalTextEn(product: CatalogProduct): string {
  return formatKrw(product.totalAmount);
}

/** `"KRW 400,000 (KRW 440,000 incl. VAT)"`. */
export function priceTextEn(product: CatalogProduct): string {
  return `${supplyTextEn(product)} (${totalTextEn(product)} incl. VAT)`;
}

/**
 * English product name, from the server.
 *
 * `nameEn` is only populated on a `locale=en` response. Falls back to the Korean
 * name: a Korean row is not good, but it beats dropping the row and quietly
 * under-reporting what LawKit sells. The server is meant to prevent this —
 * turning on English exposure without an English name is a 400.
 */
export function productNameEn(product: CatalogProduct): string {
  return product.nameEn ?? product.nameKo;
}

/**
 * English category heading.
 *
 * `categoryLabel` arrives in the requested language — `Contracts` on a `locale=en`
 * response, `계약` otherwise. There is no second field to choose between, which is
 * the point: two sources for one heading would eventually disagree.
 */
export function categoryEn(product: CatalogProduct): string {
  return product.categoryLabel;
}

/**
 * `unitLabel` arrives singular (`page`, `issue`, `item`, `hour`), so the plural is
 * made here rather than stored.
 *
 * The server would otherwise have to know every sentence a unit appears in —
 * "10 pages included" and "per additional page" want different forms of one value.
 */
function unitEn(unitLabel: string, count: number): string {
  return count === 1 ? unitLabel : `${unitLabel}s`;
}

/**
 * Surcharge terms, or `null` when the product has none.
 *
 * `quoteFrom === 0` means there is no upper bound, so no "or more" clause.
 */
export function surchargeNoteEn(product: CatalogProduct): string | null {
  const { enabled, unitLabel, included, unitAmount, quoteFrom } = product.surcharge;
  if (!enabled) return null;

  const parts = [`${included} ${unitEn(unitLabel, included)} included`, `${formatKrw(unitAmount)} per additional ${unitEn(unitLabel, 1)}`];
  if (quoteFrom > 0) parts.push(`${quoteFrom} ${unitEn(unitLabel, quoteFrom)} or more by separate quote`);
  return parts.join(' · ');
}

/**
 * Surcharge terms on the checkout screen — the English twin of `surchargeBillingNote`.
 *
 * The fee guide says what the surcharge *is*; checkout must say *how far this payment
 * goes*: the base fee covers the included volume, and anything beyond is billed after
 * the lawyer has seen the document. Volume is unknown at order time (upload is optional),
 * so only the unit price and the billing moment can be stated.
 */
export function surchargeBillingNoteEn(product: CatalogProduct): string | null {
  const { enabled, unitLabel, included, unitAmount, quoteFrom } = product.surcharge;
  if (!enabled) return null;

  const parts = [`Up to ${included} ${unitEn(unitLabel, included)} included · extra billed after review`, `${formatKrw(unitAmount)} per additional ${unitEn(unitLabel, 1)}`];
  if (quoteFrom > 0) parts.push(`${quoteFrom} ${unitEn(unitLabel, quoteFrom)} or more by separate quote`);
  return parts.join(' · ');
}

/**
 * Turnaround.
 *
 * `leadTimeLabel` arrives as free-form Korean (`"2~3영업일"`, `"일정 협의"`), so it is
 * parsed rather than looked up. Anything unrecognised falls back to
 * `leadTimeDays`, which is a number and therefore language-neutral — a new label
 * format then degrades to a correct sentence instead of printing Korean.
 */
export function leadTimeTextEn(product: CatalogProduct): string {
  const range = product.leadTimeLabel.match(/^(\d+)\s*~\s*(\d+)\s*영업일$/);
  if (range) return `${range[1]}–${range[2]} business days`;

  const single = product.leadTimeLabel.match(/^(\d+)\s*영업일$/);
  if (single) return `${single[1]} business ${Number(single[1]) === 1 ? 'day' : 'days'}`;

  if (product.leadTimeDays === 0) return 'By arrangement';
  return `Within ${product.leadTimeDays} business ${product.leadTimeDays === 1 ? 'day' : 'days'}`;
}

/**
 * Deliverables, in the requested language.
 *
 * Empty when the operator has not written the English wording yet — the server sends
 * an empty array rather than the Korean list, so this clause drops out of the sentence
 * instead of printing Korean. That gap is visible in the admin, where it can be fixed.
 */
export function deliverablesTextEn(product: CatalogProduct): string | null {
  return product.deliverables.length > 0 ? product.deliverables.join(', ') : null;
}

/**
 * Products the server routes to a consultation rather than a direct order.
 *
 * Branches on the server's `cta` only — never on `priceStatus`/`instantPayable`,
 * which is the same rule the Korean page follows. Two places deciding this would
 * eventually disagree.
 */
export function isConsultationOnlyEn(product: CatalogProduct): boolean {
  return product.cta === ('INQUIRY' satisfies Cta);
}

/**
 * The "Basis" column: consultation note → surcharge → turnaround → deliverables.
 *
 * The English pages have no order or intake funnel of their own — enquiries go
 * through the contact form in `EnMarketingLayout`. So the server's CTA branch
 * shows up as wording here rather than as a button; pointing English visitors
 * at the Korean-only `/services` and `/ask` flows would be worse than the
 * contact route they already have.
 */
export function basisTextEn(product: CatalogProduct): string {
  const consultation = isConsultationOnlyEn(product) ? 'Engaged via consultation' : null;
  return [consultation, surchargeNoteEn(product), leadTimeTextEn(product), deliverablesTextEn(product)].filter(Boolean).join(' · ');
}

/**
 * Catalog price range for structured data, on amounts payable (VAT included).
 *
 * `null` when there are no products — the caller drops the field entirely
 * rather than publishing a wrong range to search engines.
 */
export function priceRangeTextEn(products: CatalogProduct[]): string | null {
  if (products.length === 0) return null;
  const totals = products.map(p => p.totalAmount);
  return `${formatKrw(Math.min(...totals))}–${formatKrw(Math.max(...totals))}`;
}
